import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';
import { RippleModule } from 'primeng/ripple';
import { BadgeModule } from 'primeng/badge';

@Component({
     
    selector: '[app-menuitem]',
    template: `
      <ng-container>
        @if (root() && item()?.visible !== false) {
          <div class="layout-menuitem-root-text">{{ item()?.label }}</div>
        }
        @if ((!item()?.routerLink || item()?.items) && item()?.visible !== false) {
          <a
            [attr.href]="item()?.url"
            (click)="itemClick($event)"
            [class]="item()?.class || ''"
            [attr.target]="item()?.target"
            tabindex="0"
            pRipple
          >
            <i
              [class]="(item()?.icon || '') + ' layout-menuitem-icon'"
            ></i>
            <span class="layout-menuitem-text">{{ item()?.label }}</span>
            @if (item()?.badge?.(); as badge) {
              <p-badge [value]="badge" severity="danger" size="small" class="layout-menuitem-badge" />
            }
            @if (item()?.items) {
              <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
            }
          </a>
        }
        @if ((item()?.routerLink && !item()?.items) && item()?.visible !== false) {
          <a
            (click)="itemClick($event)"
            [class]="item()?.class || ''"
            [routerLink]="$safeNavigationMigration(item()?.routerLink)"
            routerLinkActive="active-route"
            [routerLinkActiveOptions]="
              item()?.routerLinkActiveOptions || {
                paths: 'exact',
                queryParams: 'ignored',
                matrixParams: 'ignored',
                fragment: 'ignored',
              }
            "
            [fragment]="$safeNavigationMigration(item()?.fragment)"
            [queryParamsHandling]="$safeNavigationMigration(item()?.queryParamsHandling)"
            [preserveFragment]="$safeNavigationMigration(item()?.preserveFragment)"
            [skipLocationChange]="$safeNavigationMigration(item()?.skipLocationChange)"
            [replaceUrl]="$safeNavigationMigration(item()?.replaceUrl)"
            [state]="$safeNavigationMigration(item()?.state)"
            [queryParams]="$safeNavigationMigration(item()?.queryParams)"
            [attr.target]="item()?.target"
            tabindex="0"
            pRipple
          >
            <i
              [class]="(item()?.icon || '') + ' layout-menuitem-icon'"
            ></i>
            <span class="layout-menuitem-text">{{ item()?.label }}</span>
            @if (item()?.badge?.(); as badge) {
              <p-badge [value]="badge" severity="danger" size="small" class="layout-menuitem-badge" />
            }
            @if (item()?.items) {
              <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
            }
          </a>
        }

        @if (item()?.items && item()?.visible !== false) {
          <ul [class.submenu-expanded]="submenuAnimation === 'expanded'">
            @for (child of item()?.items; track child; let i = $index) {
              <li
                app-menuitem
                [item]="child"
                [index]="i"
                [parentKey]="key"
                [class]="child.badgeClass"
              ></li>
            }
          </ul>
        }
      </ng-container>
    `,
    imports: [BadgeModule, RippleModule, RouterLink, RouterLinkActive],
    host: {
      '[class.layout-root-menuitem]': 'root()',
      '[class.active-menuitem]': 'active() && !root()',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppMenuitemComponent implements OnInit {
    layoutService = inject(LayoutService);
    router = inject(Router);
    private menuService = inject(MenuService);

    item = input<any>();
    index = input(0);
    root = input(false);
    parentKey = input('');

    active = signal(false);

    key = '';

    constructor() {
        effect(() => {
            const change = this.menuService.menuChange();
            if (change === null) {
                this.active.set(false);
                return;
            }
            if (change.routeEvent) {
                this.active.set(change.key === this.key || change.key.startsWith(this.key + '-'));
            } else if (change.key !== this.key && !change.key.startsWith(this.key + '-')) {
                this.active.set(false);
            }
        });

        this.router.events
          .pipe(
            filter((event) => event instanceof NavigationEnd),
            takeUntilDestroyed(),
          )
          .subscribe(() => {
            if (this.item()?.routerLink) {
              this.updateActiveStateFromRoute();
            }
          });
    }

    ngOnInit() {
        const parentKey = this.parentKey();
        const index = this.index();
        this.key = parentKey ? `${parentKey}-${index}` : String(index);

        if (this.item()?.routerLink) {
          this.updateActiveStateFromRoute();
        }
    }

    updateActiveStateFromRoute() {
        const item = this.item();
        if (!item?.routerLink?.[0]) {
          return;
        }

        const activeRoute = this.router.isActive(item.routerLink[0], {
          paths: 'exact',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored',
        });

        if (activeRoute) {
            this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
        }
    }

    itemClick(event: Event) {
        const item = this.item();
        if (item?.disabled) {
            event.preventDefault();
            return;
        }

        if (item?.command) {
            item.command({ originalEvent: event, item });
        }

        if (item?.items) {
            this.active.update(v => !v);
        }

        this.menuService.onMenuStateChange({ key: this.key });
    }

    get submenuAnimation() {
        return this.root() ? 'expanded' : (this.active() ? 'expanded' : 'collapsed');
    }
}
